'use client';
import React, { useState } from 'react';
import PublicNavbar from '../landing/components/PublicNavbar';
import PublicFooter from '../landing/components/PublicFooter';

interface FormState {
  name: string;
  email: string;
  subject: string;
  message: string;
}

const contactChannels = [
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    label: 'Email Us',
    value: 'support@muyulink.com',
    sub: 'We reply within 4 hours',
    color: 'bg-teal-50 border-teal-100 text-[#0D9488]',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
      </svg>
    ),
    label: 'Call / SMS / WhatsApp',
    value: '0726 924 637',
    sub: 'Mon–Fri, 8am–6pm EAT',
    color: 'bg-amber-50 border-amber-100 text-[#D97706]',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    label: 'Visit Us',
    value: 'Nyakio House, 1st Floor, Room 13',
    sub: 'Nairobi, Kenya',
    color: 'bg-blue-50 border-blue-100 text-blue-600',
  },
];

const faqs = [
  {
    q: 'How do I know my parcel is safe?',
    a: 'Every traveler is ID-verified and phone-verified. Payments are held in escrow until delivery is confirmed. You can track your parcel in real-time and message your traveler directly.',
  },
  {
    q: 'What if my parcel is lost or damaged?',
    a: 'MuyuRide has a dispute resolution process. If your parcel is lost or damaged, you can file a claim within 48 hours of the expected delivery date. Our team investigates and resolves within 5 business days.',
  },
  {
    q: 'How do travelers get paid?',
    a: 'Travelers receive payment via M-Pesa within 24 hours of confirmed delivery. Earnings are tracked in your traveler dashboard.',
  },
  {
    q: 'What items can I send?',
    a: 'Most everyday items — electronics, clothing, documents, food (non-perishable), and more. Prohibited items include weapons, illegal substances, and hazardous materials.',
  },
];

export default function ContactPage() {
  const [form, setForm] = useState<FormState>({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <PublicNavbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#0D2B3E] to-[#0A1628]" />
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-1/4 w-96 h-96 rounded-full bg-[#0D9488]/15 blur-[100px]" />
          <div className="absolute bottom-0 left-1/4 w-80 h-80 rounded-full bg-[#F59E0B]/10 blur-[80px]" />
        </div>
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-5">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#0D9488]/15 border border-[#0D9488]/30 text-[#4DD9CC] text-sm font-bold uppercase tracking-widest">
            Get In Touch
          </span>
          <h1 className="text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-tight">
            We&apos;re here to help.
          </h1>
          <p className="text-xl text-slate-300 max-w-xl mx-auto leading-relaxed">
            Questions about a delivery, partnership inquiries, or just want to say hello — we&apos;d love to hear from you.
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#F8FAFC] to-transparent" />
      </section>

      {/* Contact channels */}
      <section className="py-12 bg-[#F8FAFC]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {contactChannels.map((ch) => (
              <div key={ch.label} className={`rounded-2xl p-6 border ${ch.color} flex items-start gap-4 hover:-translate-y-1 hover:shadow-lg transition-all duration-300`}>
                <div className="shrink-0 mt-0.5">{ch.icon}</div>
                <div>
                  <div className="font-extrabold text-[#0F172A] text-sm">{ch.label}</div>
                  <div className="font-semibold text-[#0F172A] mt-0.5">{ch.value}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{ch.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form + FAQ */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact form */}
            <div>
              <div className="space-y-3 mb-8">
                <span className="inline-block px-4 py-1.5 rounded-full bg-teal-50 border border-teal-200 text-[#0D9488] text-sm font-bold uppercase tracking-widest">
                  Send a Message
                </span>
                <h2 className="text-3xl font-extrabold text-[#0F172A] tracking-tight">Drop us a line</h2>
                <p className="text-slate-500">We typically respond within 4 hours during business hours.</p>
              </div>

              {submitted ? (
                <div className="rounded-2xl bg-teal-50 border border-teal-200 p-10 text-center space-y-4">
                  <div className="w-16 h-16 rounded-full bg-[#0D9488] flex items-center justify-center mx-auto">
                    <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-extrabold text-[#0F172A]">Message sent!</h3>
                  <p className="text-slate-600">Thanks for reaching out. We&apos;ll get back to you at <strong>{form.email}</strong> within 4 hours.</p>
                  <button
                    onClick={() => { setSubmitted(false); setForm({ name: '', email: '', subject: '', message: '' }); }}
                    className="px-6 py-2.5 rounded-xl bg-[#0D9488] text-white font-bold text-sm hover:bg-[#0F766E] transition-colors"
                  >
                    Send Another
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="space-y-1.5">
                      <label className="text-sm font-semibold text-[#0F172A]">Full Name</label>
                      <input
                        type="text"
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Amina Kariuki"
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm text-[#0F172A] placeholder-slate-400 focus:outline-none focus:border-[#0D9488] focus:ring-2 focus:ring-[#0D9488]/10 transition-all"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-sm font-semibold text-[#0F172A]">Email Address</label>
                      <input
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="amina@example.com"
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm text-[#0F172A] placeholder-slate-400 focus:outline-none focus:border-[#0D9488] focus:ring-2 focus:ring-[#0D9488]/10 transition-all"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold text-[#0F172A]">Subject</label>
                    <select
                      value={form.subject}
                      onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm text-[#0F172A] focus:outline-none focus:border-[#0D9488] focus:ring-2 focus:ring-[#0D9488]/10 transition-all bg-white"
                    >
                      <option value="">Select a topic...</option>
                      <option value="delivery">Delivery Issue</option>
                      <option value="account">Account Help</option>
                      <option value="payment">Payment Question</option>
                      <option value="partnership">Partnership Inquiry</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold text-[#0F172A]">Message</label>
                    <textarea
                      required
                      rows={5}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="Tell us how we can help..."
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm text-[#0F172A] placeholder-slate-400 focus:outline-none focus:border-[#0D9488] focus:ring-2 focus:ring-[#0D9488]/10 transition-all resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full flex items-center justify-center gap-2.5 px-6 py-4 rounded-xl bg-[#0D9488] text-white font-bold text-base hover:bg-[#0F766E] transition-all duration-200 shadow-[0_2px_12px_rgba(13,148,136,0.3)] hover:shadow-[0_4px_20px_rgba(13,148,136,0.4)] hover:-translate-y-0.5"
                  >
                    Send Message
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  </button>
                </form>
              )}
            </div>

            {/* FAQ */}
            <div>
              <div className="space-y-3 mb-8">
                <span className="inline-block px-4 py-1.5 rounded-full bg-teal-50 border border-teal-200 text-[#0D9488] text-sm font-bold uppercase tracking-widest">
                  FAQ
                </span>
                <h2 className="text-3xl font-extrabold text-[#0F172A] tracking-tight">Common questions</h2>
                <p className="text-slate-500">Quick answers to the questions we hear most often.</p>
              </div>

              <div className="space-y-3">
                {faqs.map((faq, i) => (
                  <div
                    key={i}
                    className={`rounded-2xl border transition-all duration-300 overflow-hidden ${
                      openFaq === i ? 'border-[#0D9488]/30 bg-teal-50/50' : 'border-slate-100 bg-white hover:border-slate-200'
                    }`}
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                    >
                      <span className="font-bold text-[#0F172A] text-sm leading-snug">{faq.q}</span>
                      <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300 ${openFaq === i ? 'bg-[#0D9488] rotate-45' : 'bg-slate-100'}`}>
                        <svg className={`w-4 h-4 ${openFaq === i ? 'text-white' : 'text-slate-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                        </svg>
                      </div>
                    </button>
                    {openFaq === i && (
                      <div className="px-6 pb-5">
                        <p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Support hours */}
              <div className="mt-8 rounded-2xl bg-gradient-to-br from-[#0A1628] to-[#0D2B3E] p-6 border border-white/10">
                <h4 className="text-white font-extrabold mb-4">Support Hours</h4>
                <div className="space-y-2">
                  {[
                    { day: 'Monday – Friday', hours: '8:00 AM – 6:00 PM EAT' },
                    { day: 'Saturday', hours: '9:00 AM – 2:00 PM EAT' },
                    { day: 'Sunday', hours: 'Emergency only' },
                  ].map((row) => (
                    <div key={row.day} className="flex items-center justify-between text-sm">
                      <span className="text-slate-400">{row.day}</span>
                      <span className="text-white font-semibold">{row.hours}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-xs text-slate-400">Support team is currently online</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
